import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import api from "../services/api";
import { useSEO, SEO_PAGES } from "../hooks/useSEO";

/* ─── COLOUR PALETTE (matches Home) ─── */
const C = {
  saffron: "#E8922A", saffronL: "#FDF0D5", saffronD: "#B86A10",
  sage: "#4A7C59", sageL: "#EAF4EE", sageD: "#2D5A3D",
  terra: "#B85C38",
  cream: "#FDFAF3", creamD: "#F5EDD8",
  turmeric: "#F7C948",
  ink: "#2C1810", inkMid: "#5C3D2E", inkLight: "#9A7B60",
};

const Lotus = ({ size = 48, color = C.saffron }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    {[0,30,60,-30,-60,90].map((r,i) => (
      <ellipse key={i} cx="40" cy="50" rx="7" ry="16"
        fill={color} opacity={i > 3 ? 0.45 : i > 1 ? 0.7 : 0.95}
        transform={`rotate(${r} 40 50)`}/>
    ))}
    <circle cx="40" cy="44" r="7" fill={C.turmeric}/>
    <rect x="38" y="58" width="4" height="10" rx="2" fill={C.sage} opacity="0.55"/>
  </svg>
);

const Leaf = ({ size = 44, color = C.sage, rot = 0 }) => (
  <svg width={size} height={size} viewBox="0 0 60 60" fill="none"
    style={{ transform:`rotate(${rot}deg)` }}>
    <path d="M30 6 C52 6 58 30 30 54 C2 30 8 6 30 6Z" fill={color} opacity="0.85"/>
    <path d="M30 6 L30 54" stroke="white" strokeWidth="1.4" opacity="0.5"/>
  </svg>
);

function SectionTag({ label, light }) {
  return (
    <div style={{
      display:"inline-flex", alignItems:"center", gap:6, marginBottom:12,
      background: light ? "rgba(255,255,255,0.12)" : C.saffronL,
      border: light ? "1px solid rgba(255,255,255,0.2)" : `1px solid ${C.saffron}40`,
      borderRadius:20, padding:"4px 16px"
    }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.13em",
        color: light ? "rgba(255,255,255,0.85)" : C.saffronD,
        textTransform:"uppercase", fontFamily:"'Inter',sans-serif" }}>{label}</span>
    </div>
  );
}

export default function About() {
  useSEO(SEO_PAGES.about);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content/about")
      .then(res => setContent(res.data?.data || {}))
      .catch(() => setContent({}));
  }, []);

  const c = content || {};
  const buildList = (keys, defaults) => {
    const fromCms = keys.map(k => c[k]).filter(Boolean);
    return fromCms.length ? fromCms : defaults;
  };
  const values = buildList(["value1","value2","value3","value4"],
    ["Authenticity in Ayurveda","Ethical medical practice","Personalized patient care","Continuous learning & growth"]);
  const whyChooseUs = buildList(["whyChooseUs1","whyChooseUs2","whyChooseUs3","whyChooseUs4"],
    ["Classical Ayurvedic diagnosis & nadi pariksha","Experienced and qualified BAMS physicians","Safe, evidence-based therapies","Patient education and lifestyle guidance"]);
  const valueIcons = ["🌿","⚖️","🫁","📚"];

  return (
    <div style={{ background:C.cream, minHeight:"100vh", paddingTop:64 }}>

      {/* HERO */}
      <section style={{
        position:"relative", padding:"80px 24px 72px",
        background:`linear-gradient(145deg,${C.cream} 0%,${C.creamD} 45%,${C.saffronL} 100%)`,
        overflow:"hidden", textAlign:"center"
      }}>
        <div style={{ position:"absolute", left:"4%", top:"8%", opacity:0.12, pointerEvents:"none" }}><Leaf size={60} color={C.sage} rot={20}/></div>
        <div style={{ position:"absolute", right:"4%", top:"10%", opacity:0.05, pointerEvents:"none" }}><Lotus size={200} color={C.saffron}/></div>
        <div style={{ position:"absolute", right:"3%", bottom:"6%", opacity:0.10, pointerEvents:"none" }}><Leaf size={50} color={C.sage} rot={-30}/></div>

        <motion.div initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }}
          transition={{ duration:0.6 }} style={{ position:"relative", zIndex:2, maxWidth:720, margin:"0 auto" }}>
          <SectionTag label={c.aboutPageTag || "Who We Are"}/>
          <h1 style={{ fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(32px,5vw,58px)", fontWeight:800,
            color:C.ink, lineHeight:1.18, marginBottom:18 }}>
            {c.title || "About Heramba Ayurveda"}
          </h1>
          <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }}
            transition={{ delay:0.35, duration:0.5 }}
            style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:14, marginBottom:22 }}>
            <div style={{ height:1, width:60, background:`linear-gradient(90deg,transparent,${C.saffron})` }}/>
            <Lotus size={34} color={C.saffron}/>
            <div style={{ height:1, width:60, background:`linear-gradient(90deg,${C.saffron},transparent)` }}/>
          </motion.div>
          <p style={{ fontSize:"clamp(15px,2vw,18px)", color:C.inkMid, lineHeight:1.85,
            fontFamily:"'Merriweather',serif" }}>
            {c.description || "We practice authentic Ayurveda rooted in classical wisdom, compassionate care, and a deep respect for the ancient healing traditions of India."}
          </p>
        </motion.div>
      </section>

      {/* VISION & MISSION */}
      <section style={{ padding:"80px 24px", background:"#fff" }}>
        <div style={{ maxWidth:1100, margin:"0 auto",
          display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:32 }}>
          {[
            { tag:c.visionTag||"Our Vision", icon:"🔭", title:c.visionCardTitle||"Healing Through Holistic Care",
              text:c.vision || "To heal through holistic, natural, and personalized Ayurvedic care — restoring balance to body, mind, and spirit in every patient we serve.",
              bg:C.saffronL, border:C.saffron, accent:C.saffronD },
            { tag:c.missionTag||"Our Mission", icon:"🌱", title:c.missionCardTitle||"Patient-Centered Wellness",
              text:c.mission || "To deliver patient-centered Ayurvedic treatments that restore balance through authentic classical practices and genuine compassion.",
              bg:C.sageL, border:C.sage, accent:C.sageD }
          ].map((card, i) => (
            <motion.div key={i} initial={{ opacity:0, y:24 }}
              whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
              transition={{ delay:i*0.15 }}
              whileHover={{ y:-6, boxShadow:"0 22px 44px rgba(0,0,0,0.10)" }}
              style={{ background:card.bg, borderRadius:22, padding:"36px 32px",
                border:`1.5px solid ${card.border}30`,
                boxShadow:"0 6px 30px rgba(0,0,0,0.055)" }}>
              <div style={{ fontSize:36, marginBottom:12 }}>{card.icon}</div>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.13em",
                color:card.accent, textTransform:"uppercase",
                fontFamily:"'Inter',sans-serif", marginBottom:8 }}>{card.tag}</div>
              <h3 style={{ fontFamily:"'Playfair Display',serif",
                fontSize:22, fontWeight:700, color:C.ink, marginBottom:12 }}>{card.title}</h3>
              <p style={{ fontSize:15, color:C.inkMid, lineHeight:1.8,
                fontFamily:"'Merriweather',serif" }}>{card.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CORE VALUES */}
      <section style={{ padding:"80px 24px", background:C.cream }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }} style={{ textAlign:"center", marginBottom:48 }}>
            <SectionTag label={c.coreValuesTag || "What We Stand For"}/>
            <h2 style={{ fontFamily:"'Playfair Display',serif",
              fontSize:"clamp(26px,4vw,40px)", fontWeight:700,
              color:C.ink, margin:"8px 0 16px" }}>{c.coreValuesHeading || "Our Core Values"}</h2>
            <div style={{ width:60, height:3, borderRadius:2, margin:"0 auto",
              background:`linear-gradient(90deg,${C.saffron},${C.terra})` }}/>
          </motion.div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:24 }}>
            {values.map((v, i) => (
              <motion.div key={i} initial={{ opacity:0, y:20 }}
                whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
                transition={{ delay:i*0.1 }}
                whileHover={{ y:-6, boxShadow:"0 20px 44px rgba(0,0,0,0.10)" }}
                style={{ background:"#fff", borderRadius:20, padding:"28px 24px",
                  textAlign:"center", boxShadow:"0 4px 20px rgba(0,0,0,0.055)",
                  border:`1px solid ${C.creamD}` }}>
                <motion.div animate={{ rotate:[0,5,-5,0] }}
                  transition={{ duration:3, delay:i*0.4, repeat:Infinity, ease:"easeInOut" }}
                  style={{ fontSize:36, marginBottom:14 }}>{valueIcons[i] || "🌿"}</motion.div>
                <p style={{ fontFamily:"'Playfair Display',serif",
                  fontSize:16, fontWeight:700, color:C.ink }}>{v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section style={{
        padding:"80px 24px",
        background:`linear-gradient(135deg,${C.sageD} 0%,${C.sage} 60%,${C.sageD} 100%)`,
        position:"relative", overflow:"hidden"
      }}>
        <div style={{ position:"absolute", right:-40, top:-40, opacity:0.06 }}>
          <Lotus size={280} color="#fff"/>
        </div>
        <div style={{ maxWidth:1100, margin:"0 auto", position:"relative" }}>
          <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }} style={{ textAlign:"center", marginBottom:48 }}>
            <SectionTag label={c.whyChooseUsTag || "Why Choose Us"} light/>
            <h2 style={{ fontFamily:"'Playfair Display',serif",
              fontSize:"clamp(26px,4vw,40px)", fontWeight:700,
              color:"#fff", margin:"8px 0 16px" }}>{c.whyChooseUsHeading || "What Sets Us Apart"}</h2>
            <div style={{ width:60, height:3, borderRadius:2, margin:"0 auto",
              background:`linear-gradient(90deg,${C.turmeric},${C.saffron})` }}/>
          </motion.div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:20 }}>
            {whyChooseUs.map((item, i) => (
              <motion.div key={i} initial={{ opacity:0, y:20 }}
                whileInView={{ opacity:1, y:0 }} viewport={{ once:true }}
                transition={{ delay:i*0.1 }}
                whileHover={{ scale:1.03 }}
                style={{ background:"rgba(255,255,255,0.10)", backdropFilter:"blur(10px)",
                  borderRadius:18, padding:"24px 22px",
                  border:"1px solid rgba(255,255,255,0.15)",
                  display:"flex", alignItems:"flex-start", gap:14 }}>
                <div style={{ width:32, height:32, borderRadius:"50%", flexShrink:0,
                  background:`linear-gradient(135deg,${C.turmeric},${C.saffron})`,
                  display:"flex", alignItems:"center", justifyContent:"center", marginTop:2 }}>
                  <svg width="13" height="13" viewBox="0 0 10 10">
                    <path d="M1.5 5L4 7.5L8.5 2.5" stroke={C.ink}
                      strokeWidth="1.8" strokeLinecap="round" fill="none"/>
                  </svg>
                </div>
                <p style={{ fontSize:14, color:"rgba(255,255,255,0.88)",
                  lineHeight:1.65, fontFamily:"'Merriweather',serif" }}>{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding:"68px 24px", background:"#fff", textAlign:"center" }}>
        <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
          viewport={{ once:true }}>
          <motion.div animate={{ y:[-4,4,-4] }} transition={{ duration:3, repeat:Infinity }}>
            <Lotus size={52} color={C.saffron}/>
          </motion.div>
          <h2 style={{ fontFamily:"'Playfair Display',serif",
            fontSize:"clamp(22px,3.5vw,36px)", fontWeight:700,
            color:C.ink, margin:"16px 0 10px" }}>{c.aboutCtaHeading || "Ready to Begin Your Healing?"}</h2>
          <p style={{ fontSize:16, color:C.inkLight, marginBottom:28,
            fontFamily:"'Merriweather',serif" }}>
            {c.aboutCtaSub || "Book a consultation and experience the difference of authentic Ayurvedic care."}
          </p>
          <motion.a href="/request"
            whileHover={{ scale:1.04, boxShadow:"0 14px 34px rgba(232,146,42,0.50)" }}
            whileTap={{ scale:0.97 }}
            style={{ display:"inline-flex", alignItems:"center", gap:8,
              padding:"13px 32px", borderRadius:50, fontSize:15, fontWeight:600,
              textDecoration:"none", fontFamily:"'Inter',sans-serif",
              background:`linear-gradient(135deg,${C.saffron},${C.terra})`,
              color:"#fff", boxShadow:`0 8px 26px rgba(232,146,42,0.38)` }}>
            🌿 {c.aboutCtaBtn || "Book Appointment"}
          </motion.a>
        </motion.div>
      </section>

    </div>
  );
}
